import React, { Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { LoadingBackdrop } from './components/LoadingBackdrop';
import { HomePage } from './pages/HomePage/HomePage';

const getPageLazily = (path: string, name: string) =>
  React.lazy(() =>
    import(`./${path}/${name}/${name}`).then((module) => ({
      default: module[name],
    })),
  );

/** Supports */
const SupportsPage = getPageLazily('pages', 'SupportsPage');

/** Applications */
const VenueApplicationsPage = getPageLazily('pages', 'VenueApplicationsPage');
const UserApplicationsPage = getPageLazily('pages', 'UserApplicationsPage');
const OfferApplicationsPage = getPageLazily('pages', 'OfferApplicationsPage');

/** Markets */
const MarketsPage = getPageLazily('pages', 'MarketsPage');
const MarketsEditPage = getPageLazily('pages', 'MarketsEditPage');
const MarketsProfilePage = getPageLazily('pages', 'MarketsProfilePage');

/** Users */
const UsersPage = getPageLazily('pages', 'UsersPage');
const UsersEditPage = getPageLazily('pages', 'UsersEditPage');
const UserProfilePage = getPageLazily('pages', 'UserProfilePage');

/** Users Chats */
const ChatsPage = getPageLazily('pages', 'ChatsPage');

/** Venues */
const VenuesPage = getPageLazily('pages', 'VenuesPage');
const VenuesEditPage = getPageLazily('pages', 'VenuesEditPage');
const VenuesProfilePage = getPageLazily('pages', 'VenuesProfilePage');

/** Subscriptions */
const SubscriptionsPage = getPageLazily('pages', 'SubscriptionsPage');

/** Agencies */
const AgenciesPage = getPageLazily('pages', 'AgenciesPage');
const AgenciesEditPage = getPageLazily('pages', 'AgenciesEditPage');
const AgencyProfilePage = getPageLazily('pages', 'AgencyProfilePage');

/** Offers */
const OffersPage = getPageLazily('pages', 'OffersPage');
const OffersEditPage = getPageLazily('pages', 'OffersEditPage');
const OffersProfilePage = getPageLazily('pages', 'OffersProfilePage');

/** Events */
const EventPage = getPageLazily('pages', 'EventPage');

/** Invites */
const InvitesPage = getPageLazily('pages', 'InvitesPage');
const InvitesDetailsPage = getPageLazily('pages', 'InvitesDetailsPage');

/** FastCheckReservations */
const FastCheckPage = getPageLazily('pages', 'FastCheckPage');

/** Reservations */
const ReservationsPage = getPageLazily('pages', 'ReservationsPage');
const ReservationsDetailsPage = getPageLazily('pages', 'ReservationsDetailsPage');

/** Others */
const NoShowsPage = getPageLazily('pages', 'NoShowsPage');
const PenaltiesPage = getPageLazily('pages', 'PenaltiesPage');
const VenueLeadsPage = getPageLazily('pages', 'VenueLeadsPage');
const VenueLeadDetailsPage = getPageLazily('pages', 'VenueLeadDetailsPage');

/** Suspensions */
const SuspensionsPage = getPageLazily('pages', 'SuspensionsPage');
const SuspensionsCreatePage = getPageLazily('pages', 'SuspensionsCreatePage');

/** Campaigns */
const CampaignsPage = getPageLazily('pages', 'CampaignsPage');
const CampaignsEditPage = getPageLazily('pages', 'CampaignsEditPage');

/** Staff */
const StaffPage = getPageLazily('pages', 'StaffPage');
const StaffEditPage = getPageLazily('pages', 'StaffEditPage');

const ContentPage = getPageLazily('pages', 'ContentPage');

/** Leaderboard */
const LeaderboardPage = getPageLazily('pages', 'LeaderboardPage');

const LogsPage = getPageLazily('pages', 'LogsPage');

const TermsAndConditionsPage = getPageLazily('pages', 'TermsAndConditionsPage');

/** Cohorts */
const CohortsPage = getPageLazily('pages', 'CohortsPage');

/** Instagram Accounts */
const InstagramAccountsPage = getPageLazily('pages', 'InstagramAccountsPage');

/** Scouting page */
const ScoutingPage = getPageLazily('pages', 'ScoutingPage');

/** Search Businesses */
const SearchBusinessesPage = getPageLazily('pages', 'SearchBusinessesPage');

/** Customer Support Research */
const CustomerSupportResearchPage = getPageLazily('pages', 'CustomerSupportResearchPage');

export const Routes = (
  <Suspense fallback={<LoadingBackdrop loading dimBackground={false} />}>
    <Switch>
      <Route path="/" exact component={HomePage} />
      <Route path="/profile-updates" exact component={SupportsPage} />
      {/* Applications */}
      <Route path="/user-applications" exact component={UserApplicationsPage} />
      <Route path="/business-applications" exact component={VenueApplicationsPage} />
      <Route path="/offer-applications" exact component={OfferApplicationsPage} />
      {/* Markets */}
      <Route path="/markets" exact component={MarketsPage} />
      <Route path={['/markets/new', '/markets/:id']} exact component={MarketsEditPage} />
      <Route path="/markets/:id/profile" exact component={MarketsProfilePage} />
      {/* Users */}
      <Route path="/users" exact component={UsersPage} />
      <Route
        path={['/users/new', '/users/:id', '/user-applications/:id']}
        exact
        component={UsersEditPage}
      />
      <Route
        path={['/users/:id/profile', '/user-applications/:id/profile']}
        exact
        component={UserProfilePage}
      />
      {/* Users Chats */}
      <Route path="/chats" exact component={ChatsPage} />
      {/* <Route path="/users-chats/:id/conversation" exact component={UsersChatConversationPage} /> */}
      {/* Venues */}
      <Route path="/businesses" exact component={VenuesPage} />
      <Route
        path={['/businesses/new', '/businesses/:id', '/business-applications/:id']}
        exact
        component={VenuesEditPage}
      />
      <Route
        path={['/businesses/:id/profile', '/business-applications/:id/profile']}
        exact
        component={VenuesProfilePage}
      />
      {/* Subscriptions */}
      <Route path="/subscriptions" exact component={SubscriptionsPage} />
      {/* Agencies */}
      <Route path="/agencies" exact component={AgenciesPage} />
      <Route
        path={['/agencies/new', '/agencies/:id', '/agency-applications/:id']}
        exact
        component={AgenciesEditPage}
      />
      <Route
        path={['/agencies/:id/profile', '/agency-applications/:id/profile']}
        exact
        component={AgencyProfilePage}
      />
      {/* Offers */}
      <Route path="/offers" exact component={OffersPage} />
      <Route
        path={['/offers/new', '/offers/:id', '/offer-applications/:id']}
        exact
        component={OffersEditPage}
      />
      <Route
        path={['/offers/:id/profile', '/offer-applications/:id/profile']}
        exact
        component={OffersProfilePage}
      />

      {/* Events */}
      <Route path="/event" exact component={EventPage} />

      {/* Invites */}
      <Route path="/collabs" exact component={InvitesPage} />
      <Route path="/collabs/:id" exact component={InvitesDetailsPage} />
      {/* Reservations */}
      <Route path="/reservations" exact component={ReservationsPage} />
      <Route path="/fast-check-reservations" exact component={FastCheckPage} />

      <Route path="/reservations/:id" exact component={ReservationsDetailsPage} />
      {/* Others */}
      <Route path="/core-action" exact component={CohortsPage} />
      <Route path="/no-shows" exact component={NoShowsPage} />
      <Route path="/suspensions" exact component={SuspensionsPage} />
      <Route
        path={['/suspensions/new', '/suspensions/:id']}
        exact
        component={SuspensionsCreatePage}
      />
      <Route path="/penalties" exact component={PenaltiesPage} />
      <Route path="/content" exact component={ContentPage} />
      <Route path="/leaderboard" exact component={LeaderboardPage} />
      <Route path="/referrals" exact component={VenueLeadsPage} />
      <Route path="/referrals/:id" exact component={VenueLeadDetailsPage} />
      <Route path="/campaigns" exact component={CampaignsPage} />
      <Route path={['/campaigns/new', '/campaigns/:id']} exact component={CampaignsEditPage} />
      <Route path="/staff" exact component={StaffPage} />
      <Route path={['/staff/new', '/staff/:id']} exact component={StaffEditPage} />
      <Route path="/logs" exact component={LogsPage} />
      <Route path="/terms-and-conditions" exact component={TermsAndConditionsPage} />
      <Route path="/instagram-accounts" exact component={InstagramAccountsPage} />
      <Route path="/scouting" exact component={ScoutingPage} />
      <Route path="/search-businesses" exact component={SearchBusinessesPage} />
      <Route path="/support-ai" exact component={CustomerSupportResearchPage} />
      <Redirect to="/" />
    </Switch>
  </Suspense>
);
